import React, { useContext, useState } from 'react';
import { translate, t } from '../../../../types/translation/Translator';
import { AssetExportConfigurationContext } from '../../../../context/AssetExportConfigurationContext';
import { ExportItems } from '../../../components/ExportItems';
import { Routing } from '../../../../util/routes.util';
import { AssetExportConfigurationRequest } from '../../../../requests/assetExportConfiguration.request';
import { useRequest } from '../../../../requests/common/requestHook';
import { TranslationKey } from '../../../../types/translation/TranslationKey';
import { ActionModal } from '../../../components/Modal';
import { Button, CancelButton } from '../../../components/Button';
import moment from 'moment';
import { ExportUtil } from '../../../../util/export.util';
import { ExportFile } from '../../../../types/export/export';
import { User } from '../../../../types/user';
import { UserContext } from '../../../../context/UserContext';

interface AssetExportModalProps {
  open: boolean;
  setOpen: (open: boolean) => void;
}

export default function AssetExportModal({ open, setOpen }: AssetExportModalProps) {
  const { assetExportConfigurations } = useContext(AssetExportConfigurationContext);
  const { currentUser } = useContext(UserContext);

  const [exportAssets, { loading }] = useRequest(AssetExportConfigurationRequest.download);

  const [selected, setSelected] = useState<Set<string>>(
    new Set([...assetExportConfigurations.values()].filter(config => config.default).map(config => config.id)),
  );

  const handleClose = () => {
    setOpen(false);
  };

  const handleDownload = async () => {
    const locale = User.locale(currentUser);

    if (!selected.size) {
      const file = await exportAssets({
        timezone: moment.tz.guess(),
        locale,
      });

      if (!file) return;
      ExportUtil.downloadExport(file);
    } else {
      const files: ExportFile[] = [];
      for (const configId of Array.from(selected)) {
        const file = await exportAssets({
          configId,
          timezone: moment.tz.guess(),
          locale,
        });

        if (file) files.push(file);
      }

      if (!files.length) return;

      if (files.length === 1) {
        ExportUtil.downloadExport(files[0]);
      } else {
        ExportUtil.downloadZip(files, assetExportConfigurations);
      }
    }

    setSelected(new Set());
    handleClose();
  };

  const footer = (
    <div className='flex justify-end gap-2'>
      <CancelButton onClick={handleClose} disabled={loading} />
      <Button loading={loading} text={translate({ key: TranslationKey.export })} onClick={handleDownload} />
    </div>
  );

  return (
    <ActionModal
      footer={footer}
      title={`${translate({ key: TranslationKey.export })} ${t(TranslationKey.asset).plural.label}`}
      open={open}
      setOpen={setOpen}
      className='max-w-[720px]'
    >
      <ExportItems
        items={[...assetExportConfigurations.values()]}
        selected={selected}
        setSelected={setSelected}
        createPath={Routing.assets.settings.report.create()}
      />
    </ActionModal>
  );
}
